import React, { ReactElement } from "react";
import {
    ChatStatus as ApiChatStatus,
} from "../../../__generated__/api";
import { Filters } from "../../filters/Filters";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface ChatStatusProps<TIsMulti = false, TIsClearable = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    isClearable?: TIsClearable;
    shouldShowDropdown?: boolean;
    className?: string;
    toggleClassName?: string;
    toggleText?: string
    value: (TIsMulti extends true ? ApiChatStatus[] : ApiChatStatus) | undefined;
    onChange: (
        sourceId: (TIsMulti extends true ? ApiChatStatus[] : ApiChatStatus) | (TIsClearable extends true ? undefined : never),
        display: string
    ) => void;
}

export const STATUS_TEXT = {
    [ApiChatStatus.Assigned]: "Assigned",
    [ApiChatStatus.ChatRequested]: "User requesting chat",
    [ApiChatStatus.Finished]: "Finished",
    [ApiChatStatus.Ignored]: "Ignored",
    [ApiChatStatus.OnHold]: "On Hold",
    [ApiChatStatus.Snoozed]: "Snoozed",
    [ApiChatStatus.Connected]: "User Connected",
    [ApiChatStatus.DeflectionWaiting]: "Deflection Waiting",
};

const ChatStatus = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onChange, placeholder, minRequired, value, className, shouldShowDropdown, toggleText, toggleClassName, isClearable,
}: ChatStatusProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    type SelectedType = TIsMulti extends true ? readonly Option<string>[] : Option<string>;
    type ValueType = TIsMulti extends true ? Option<string>[] : Option<string>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as ApiChatStatus)
            : (selected as Option<string>)?.value) as Parameters<typeof onChange>[0];

        const text = (Array.isArray(selected)
            ? selected.map(row => row.label)
            : (selected as Option<string>)?.label) as Parameters<typeof onChange>[1];
        onChange(values, text);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    const selected = Array.isArray(value)
        ? value.map(row => ({
            value: row,
            label: STATUS_TEXT[row],
        })).filterNull()
        : {
            value,
            label: STATUS_TEXT[value as ApiChatStatus],
        };
    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                categories={Object.entries(STATUS_TEXT).map(([id, label]) => ({
                    value: id,
                    label,
                }))}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti ? new Set(value) : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onChange as ((value: string[]) => void))(Array.from(current))
                    : (onChange as ((value: string) => void))(current as string))}
            />
        );
    }
    return (
        <Select
            ref={ref}
            className={className}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={Object.entries(STATUS_TEXT).map(([key, label]) => ({
                label,
                value: key,
            }))}
            value={selected as ValueType}
            isClearable={isClearable}

        />
    );
});

ChatStatus.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
    shouldShowDropdown: undefined,
    className: undefined,
    toggleClassName: undefined,
    toggleText: undefined,
};

export default ChatStatus as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
> (
    props: ChatStatusProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof ChatStatus>;
